/**
 * Layout
 *
 * Application shell for all authenticated pages.
 *
 * DESKTOP (lg+): sidebar left + content right, no mobile header, no bottom nav
 * MOBILE (<lg):  mobile header top + content + bottom tab bar, no sidebar
 *
 * All responsive behavior is handled by Tailwind utility classes directly
 * on the elements. No separate CSS file. The only custom CSS class used is
 * `pb-safe` (defined in index.css) for iOS safe area on the bottom nav.
 *
 * This is a DUMB component — it renders what it receives via props.
 * Nav items should be filtered by role BEFORE passing them here.
 *
 * Uses React Router v6 <Outlet /> for nested route rendering.
 */
import { useState, useRef, useEffect, ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LOCALES } from '../i18n/i18n';
import ThemeToggle from './ThemeToggle';
import { useAuth } from '../hooks/useAuth';

const LOCALE_FLAGS: Record<string, string> = {
  en: '\u{1F1EC}\u{1F1E7}', sk: '\u{1F1F8}\u{1F1F0}', cs: '\u{1F1E8}\u{1F1FF}',
  de: '\u{1F1E9}\u{1F1EA}', fr: '\u{1F1EB}\u{1F1F7}', es: '\u{1F1EA}\u{1F1F8}',
  it: '\u{1F1EE}\u{1F1F9}', pt: '\u{1F1F5}\u{1F1F9}', ja: '\u{1F1EF}\u{1F1F5}',
  zh: '\u{1F1E8}\u{1F1F3}', ko: '\u{1F1F0}\u{1F1F7}',
};

// =============================================================================
// Types
// =============================================================================

export interface NavItem {
  label: string;
  path: string;
  icon?: ReactNode;
  group?: string;
}

export interface UserMenuItem {
  label: string;
  path?: string;
  separator?: boolean;
  onClick?: () => void;
}

export interface LayoutProps {
  appName?: string;
  navItems?: NavItem[];
  userMenuItems?: UserMenuItem[];
  logoUrl?: string;
  breadcrumb?: ReactNode;
  maxMobileNavItems?: number;
}

// =============================================================================
// Subcomponents
// =============================================================================

/** Default icon when NavItem.icon is not provided */
function DefaultNavIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
    </svg>
  );
}

/** User icon (used in sidebar avatar) */
function UserIcon() {
  return (
    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0" />
    </svg>
  );
}

/** Chevron up-down icon (used in user menu trigger) */
function ChevronUpDownIcon() {
  return (
    <svg className="w-4 h-4 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
    </svg>
  );
}

/** Three-dot vertical icon (used in mobile header menu) */
function EllipsisVerticalIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z" />
    </svg>
  );
}

/** Language switcher rendered inside the user menu dropdown */
function LanguageSwitcherInMenu() {
  const { i18n } = useTranslation();
  const currentLang = i18n.language?.substring(0, 2) || 'en';

  // Don't render if only one locale is configured
  if (SUPPORTED_LOCALES.length <= 1) return null;

  return (
    <>
      <div className="border-t border-[var(--border-subtle)] my-1" />
      <div className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">
        Language
      </div>
      {SUPPORTED_LOCALES.map((code) => (
        <button
          key={code}
          onClick={() => i18n.changeLanguage(code)}
          className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
            currentLang === code
              ? 'bg-[var(--surface-raised)] text-[var(--accent-primary)]'
              : 'text-[var(--text-secondary)] hover:bg-[var(--surface-raised)]'
          }`}
        >
          {LOCALE_FLAGS[code] || ''} {code.toUpperCase()}
        </button>
      ))}
    </>
  );
}

/** Dropdown menu items — shared between desktop user menu and mobile menu */
function MenuItemList({
  items,
  onClose,
}: {
  items: UserMenuItem[];
  onClose: () => void;
}) {
  return (
    <>
      {items.map((item, idx) => (
        <div key={idx}>
          {item.separator && (
            <div className="border-t border-[var(--border-subtle)] my-1" />
          )}
          {item.path ? (
            <NavLink
              to={item.path}
              className="block w-full text-left px-4 py-2.5 text-sm text-[var(--text-secondary)] hover:bg-[var(--surface-raised)] hover:text-[var(--text-primary)] transition-colors"
            >
              {item.label}
            </NavLink>
          ) : (
            <button
              type="button"
              onClick={() => {
                onClose();
                item.onClick?.();
              }}
              className="block w-full text-left px-4 py-2.5 text-sm text-[var(--text-secondary)] hover:bg-[var(--surface-raised)] hover:text-[var(--text-primary)] transition-colors"
            >
              {item.label}
            </button>
          )}
        </div>
      ))}
      <LanguageSwitcherInMenu />
      <div className="border-t border-[var(--border-subtle)] my-1" />
      <div className="px-4 py-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Appearance</span>
        <ThemeToggle />
      </div>
    </>
  );
}

// =============================================================================
// Main Component
// =============================================================================

function Layout({
  appName = 'App',
  navItems = [],
  userMenuItems = [],
  logoUrl,
  breadcrumb,
  maxMobileNavItems = 5,
}: LayoutProps) {
  const location = useLocation();
  const { user } = useAuth();
  const accountLabel =
    (user && (user.name as string)) ||
    (user && (user.email as string)) ||
    'Account';
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Close menus on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Group nav items by group for sidebar sections
  const groupedNavItems = navItems.reduce<Record<string, NavItem[]>>((acc, item) => {
    const key = item.group || '';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  // Bottom tab bar gets first N items
  const mobileNavItems = navItems.slice(0, maxMobileNavItems);

  return (
    // Outer container: column on mobile, row on desktop
    <div className="flex flex-col lg:flex-row min-h-screen bg-[var(--surface-ground)]">

      {/* ================================================================= */}
      {/* DESKTOP SIDEBAR — hidden below lg, sticky sidebar at lg+          */}
      {/* ================================================================= */}
      <aside className="hidden lg:flex lg:flex-col lg:w-56 lg:flex-shrink-0 lg:h-screen lg:sticky lg:top-0 bg-[var(--surface-base)] lg:border-r border-[var(--border-subtle)]">
        {/* Sidebar header */}
        <div className="h-14 flex items-center px-4 border-b border-[var(--border-subtle)]">
          <NavLink to="/" className="flex items-center gap-3 min-w-0">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={appName}
                className="h-8 w-8 object-contain flex-shrink-0"
              />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-[var(--accent-primary)] flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-sm">
                  {appName.charAt(0)}
                </span>
              </div>
            )}
            <span className="font-semibold text-[var(--text-primary)] text-sm truncate">
              {appName}
            </span>
          </NavLink>
        </div>

        {/* Sidebar navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1 scrollbar-thin">
          {Object.entries(groupedNavItems).map(([group, items]) => (
            <div key={group}>
              {group && (
                <p className="px-3 mt-4 mb-1 text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">
                  {group}
                </p>
              )}
              {items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-[var(--accent-primary)]/20 text-[var(--accent-primary)]'
                        : 'text-[var(--text-muted)] hover:bg-[var(--surface-raised)] hover:text-[var(--text-primary)]'
                    }`
                  }
                >
                  {item.icon && (
                    <span className="w-5 h-5 flex-shrink-0" aria-hidden="true">
                      {item.icon}
                    </span>
                  )}
                  <span className="truncate">{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* Sidebar footer — user menu (opens upward) */}
        {userMenuItems.length > 0 && (
          <div className="border-t border-[var(--border-subtle)] p-3" ref={userMenuRef}>
            <div className="relative">
              <button
                type="button"
                onClick={() => setUserMenuOpen((prev) => !prev)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[var(--surface-raised)] transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-[var(--accent-primary)] flex items-center justify-center flex-shrink-0">
                  <UserIcon />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <div className="text-sm font-medium text-[var(--text-primary)] truncate">
                    {accountLabel}
                  </div>
                </div>
                <ChevronUpDownIcon />
              </button>

              {userMenuOpen && (
                <div className="absolute bottom-full left-0 right-0 mb-2 z-50 bg-[var(--surface-overlay)] border border-[var(--border-subtle)] rounded-lg shadow-xl overflow-hidden">
                  <MenuItemList
                    items={userMenuItems}
                    onClose={() => setUserMenuOpen(false)}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </aside>

      {/* ================================================================= */}
      {/* MAIN CONTENT WRAPPER                                              */}
      {/* ================================================================= */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* MOBILE HEADER — visible below lg, hidden at lg+ */}
        <header className="sticky top-0 z-30 h-14 flex items-center justify-between px-4 bg-[var(--surface-base)] border-b border-[var(--border-subtle)] lg:hidden">
          <div className="flex-1" />
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={appName}
              className="h-10 max-w-[180px] object-contain"
            />
          ) : (
            <span className="text-[var(--text-primary)] font-semibold text-sm">
              {appName}
            </span>
          )}
          <div className="flex-1 flex justify-end">
            {userMenuItems.length > 0 && (
              <div className="relative" ref={mobileMenuRef}>
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen((prev) => !prev)}
                  className="p-2 rounded-lg text-[var(--text-muted)] hover:bg-[var(--surface-raised)] hover:text-[var(--text-primary)] transition-colors"
                >
                  <EllipsisVerticalIcon />
                </button>

                {mobileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-[var(--surface-overlay)] border border-[var(--border-subtle)] rounded-lg shadow-xl overflow-hidden z-50">
                    <MenuItemList
                      items={userMenuItems}
                      onClose={() => setMobileMenuOpen(false)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </header>

        {/* PAGE CONTENT — extra bottom padding on mobile for fixed bottom nav */}
        <main className="flex-1 overflow-auto pb-20 lg:pb-0">
          <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            {breadcrumb && <div className="mb-4">{breadcrumb}</div>}
            <Outlet />
          </div>
        </main>
      </div>

      {/* ================================================================= */}
      {/* MOBILE BOTTOM TAB BAR — fixed bottom, visible below lg            */}
      {/* pb-safe = env(safe-area-inset-bottom) for iPhone gesture bar      */}
      {/* ================================================================= */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-[var(--surface-base)] border-t border-[var(--border-subtle)] pb-safe lg:hidden">
        <div className="flex items-center justify-around h-16 px-2">
          {mobileNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center flex-1 py-2 transition-colors min-w-0 ${
                  isActive
                    ? 'text-[var(--accent-primary)]'
                    : 'text-[var(--text-muted)]'
                }`
              }
            >
              <span className="w-6 h-6 flex-shrink-0" aria-hidden="true">
                {item.icon || <DefaultNavIcon />}
              </span>
              <span className="text-xs mt-1 truncate w-full text-center px-1">
                {item.label}
              </span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

export { Layout };
export default Layout;